import { useCallback, useState } from 'react'
import { styled } from '@mui/material'
import { colorPalette } from '../../../../../style/colorPalette'
import { intl } from '../../../../../../i18n'
import { EntityIcon } from '../../EntityIcon'

type SelectedChip = {
  key: string
  label: string
  iconUrl?: string
}

// 工程/チケット管理表のように表示が入れ替わるフィールドは切替でこのコンポーネント自体が
// 作り直されるため、開閉状態を保ちたい場合は呼び出し側で状態を持つ。
// 状態とハンドラを1つにまとめ、片方だけ渡して操作できなくなる事故を防ぐ
export type CollapsibleOpenState = {
  isOpen: boolean
  onToggle: () => void
}

type CollapsibleSelectFieldProps = {
  chips: SelectedChip[]
  emptyLabel: string
  children: React.ReactNode
  // チーム選択のように削除・並び替えを許すフィールドは、呼び出し側でチップ表示自体を
  // 差し替える(ドラッグ&ドロップ・ホバーで削除ボタンを出す等)
  renderChips?: (chips: SelectedChip[]) => React.ReactNode
  // 未指定ならこのコンポーネント内部で開閉状態を持つ
  openState?: CollapsibleOpenState
}

// 既に選択されている値をチップで常時表示し、選択操作自体は「変更する」ボタンで明示的に開閉する
export const CollapsibleSelectField = ({
  chips,
  emptyLabel,
  children,
  renderChips,
  openState,
}: CollapsibleSelectFieldProps) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = openState ? openState.isOpen : internalOpen

  const handleToggleOpen = useCallback(() => {
    if (openState) {
      openState.onToggle()
      return
    }
    setInternalOpen(prev => !prev)
  }, [openState])

  return (
    <Container>
      <SummaryRow>
        <ChipsArea>
          {chips.length === 0 ? (
            <EmptyLabel>{emptyLabel}</EmptyLabel>
          ) : renderChips ? (
            renderChips(chips)
          ) : (
            chips.map(chip => (
              <Chip key={chip.key}>
                <EntityIcon src={chip.iconUrl} size={18} />
                {chip.label}
              </Chip>
            ))
          )}
        </ChipsArea>
        <ChangeButton type="button" onClick={handleToggleOpen}>
          {open
            ? intl.formatMessage({ id: 'versionReport.treeSelect.close' })
            : chips.length === 0
              ? intl.formatMessage({ id: 'versionReport.treeSelect.select' })
              : intl.formatMessage({ id: 'versionReport.treeSelect.change' })}
        </ChangeButton>
      </SummaryRow>
      {open && <Body>{children}</Body>}
    </Container>
  )
}

const Container = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  background: colorPalette.monotone[0],
})

const SummaryRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '8px 10px',
  minHeight: '20px',
})

const ChipsArea = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '5px',
})

const EmptyLabel = styled('span')({
  fontSize: '12.5px',
  color: colorPalette.monotone[4],
})

const Chip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  background: colorPalette.monotone[1],
  color: colorPalette.monotone[5],
  borderRadius: '15px',
  padding: '3px 9px',
  fontSize: '12px',
})

const ChangeButton = styled('button')({
  flexShrink: 0,
  fontFamily: 'inherit',
  border: `1px solid ${colorPalette.monotone[2]}`,
  background: colorPalette.monotone[0],
  color: colorPalette.skyBlue[8],
  fontSize: '11.5px',
  fontWeight: 500,
  padding: '4px 10px',
  borderRadius: '4px',
  cursor: 'pointer',
  '&:hover': {
    background: colorPalette.skyBlue[1],
    borderColor: colorPalette.skyBlue[2],
  },
})

const Body = styled('div')({
  borderTop: `1px solid ${colorPalette.monotone[2]}`,
  padding: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})
